<script setup lang="ts">
/**
 * **分组行**：可折叠的区段标题，整行可点。
 *
 * 折叠时要把已连接的线**锚定**在分组行上（`anchor-input` / `anchor-output`
 * 插槽）—— 不锚的话挂在这个分组里的线会失去落点。
 */
import type { GroupRow, SocketDef } from '../../types/node'

const props = defineProps<{
  group: GroupRow
  collapsed: boolean
  memberCount: number
  /** 折叠时挂在分组行上的端口（已按方向过滤好） */
  anchorsIn: readonly SocketDef[]
  anchorsOut: readonly SocketDef[]
}>()

const emit = defineEmits<{ (e: 'toggle'): void }>()
</script>

<template>
  <div
    class="np-row np-row--group"
    data-row-kind="group"
    :class="{ 'is-collapsed': props.collapsed }"
    role="button"
    tabindex="0"
    :aria-expanded="!props.collapsed"
    @pointerdown.stop
    @click.stop="emit('toggle')"
    @keydown.enter.prevent="emit('toggle')"
    @keydown.space.prevent="emit('toggle')"
  >
    <span class="np-group__caret" aria-hidden="true">
      <svg viewBox="0 0 10 10" fill="none">
        <path
          d="M1.5 3.2 L5 6.8 L8.5 3.2"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>

    <span class="np-group__label">{{ props.group.label }}</span>

    <!--
      只有非空分组才显示成员数。空分组仍渲染标题（只是不显示数字），
      这样能一眼区分「没有这个分组」和「分组里没属性」。
    -->
    <span v-if="props.memberCount" class="np-group__count">
      {{ props.memberCount }}
    </span>

    <!--
      折叠时把已连接的线锚定到分组行：不渲染圆点、不可拖拽，
      且所有锚点落在同一位置（不做错开）。
    -->
    <!-- 传副本：宿主插槽声明的是可变数组，readonly 传不过去 -->
    <slot name="anchor-input" :group="props.group" :sockets="[...props.anchorsIn]" />
    <slot name="anchor-output" :group="props.group" :sockets="[...props.anchorsOut]" />
  </div>
</template>
